import { z } from 'zod';

import { CostSchema, LabeledChargeSchema } from './shared-schemas.js';

/**
 * Lead guest captured at checkout. Used as the named guest on every Sabre call
 * for a HotelBooking, and as the contact + customer details for the PNR.
 *
 * The Sabre PNR-create schema does not require enumerating every occupant by
 * name; only the lead is required. See `docs/sabre/.../create-passenger-name-record.yaml`.
 */
export const LeadGuestSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().optional(),
  email: z.string().email(),
  phone: z.string(),
  loyaltyId: z.string().optional(),
});

export type LeadGuest = z.infer<typeof LeadGuestSchema>;

/**
 * The customer's (lead guest's) home / mailing address. Pre-filled from the
 * user's profile and editable at checkout. Sent to Sabre as the customer
 * address on the PNR (NOT the agency address — Elara's agency address is
 * configured separately on the Sabre client). `city` and `countryCode` are
 * required by Sabre; `stateProvinceCode` is required for US addresses.
 * Optional in storage; enforced at submit time.
 */
export const BookingAddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  stateProvinceCode: z.string().optional(),
  postalCode: z.string().optional(),
  countryCode: z.string().optional(),
});

export type BookingAddress = z.infer<typeof BookingAddressSchema>;

/**
 * Per-room occupancy. Sabre's PriceCheck takes Adults + Children + ChildAges.
 * Children are stored as objects so future fields (e.g. firstName for hotels
 * that require named children) can be added without a migration.
 */
export const OccupancySchema = z.object({
  adults: z.number().int().min(1),
  children: z.array(z.object({ age: z.number().int().min(0).max(17) })).default([]),
});

export type Occupancy = z.infer<typeof OccupancySchema>;

/**
 * A user-selected room extra (crib, rollaway, etc.). References the rate's
 * extras catalog by id and carries the sabreCode for the eventual Sabre call.
 */
export const SelectedRoomExtraSchema = z.object({
  extraId: z.string(),
  sabreCode: z.number().optional(),
  quantity: z.number().int().min(1),
});

export type SelectedRoomExtra = z.infer<typeof SelectedRoomExtraSchema>;

/**
 * A room extra the guest is *eligible* to add, resolved server-side at
 * checkout-prep time. The server collapses the occupancy-aware eligibility rule
 * (Sabre codes 26=crib / 91=rollaway / 196=extra-adult, under-4 vs ≥4 age
 * bands, multi-adult condition) AND the per-extra cap into a single verdict so
 * every client renders an identical, correct picker instead of re-deriving the
 * rule from the live rate catalog. `maxQuantity` is the final selectable cap.
 */
export const EligibleRoomExtraSchema = z.object({
  id: z.string(),
  sabreRoomExtraCode: z.number().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  amount: z.number().optional(),
  currency: z.string().optional(),
  scope: z.enum(['per_stay', 'per_night', 'per_person']).optional(),
  /** Maximum quantity the guest may select (eligibility + cap collapsed into one). */
  maxQuantity: z.number().int().positive(),
});

export type EligibleRoomExtra = z.infer<typeof EligibleRoomExtraSchema>;

/** The eligible extras for one prepared room, keyed back to its itinerary item. */
export const RoomEligibleExtrasSchema = z.object({
  itineraryItemId: z.string(),
  extras: z.array(EligibleRoomExtraSchema),
});

export type RoomEligibleExtras = z.infer<typeof RoomEligibleExtrasSchema>;

/**
 * Status of one room within a HotelBooking. Each Sabre call corresponds to
 * one entry in `HotelBooking.rooms[]`, with its own confirmation and policy.
 */
export const HotelBookingRoomStatusSchema = z.enum(['pending', 'booked', 'failed', 'cancelled']);
export type HotelBookingRoomStatus = z.infer<typeof HotelBookingRoomStatusSchema>;

/**
 * How a booking (itinerary item or checkout room) came to exist: made by Elara through a GDS
 * (`sabre`, `travelport`), a flight the traveler booked themselves with the provider (`direct`), or
 * a stay recorded by hand / made outside Elara (`manual`). Trimmed 2026-09-18 from the initial-commit
 * wish-list (google, amadeus, virtuoso, fhr) — none was ever written or read; Virtuoso and FHR are
 * hotel affiliations, not channels.
 */
export const BookingChannelSchema = z.enum(['sabre', 'travelport', 'direct', 'manual']);
export type BookingChannel = z.infer<typeof BookingChannelSchema>;

/**
 * Channels Elara books THROUGH a GDS on the traveler's behalf: the reservation is a live PNR the
 * server created, so it is cancelled in-app (`CancelHotelBooking`) and never edited by hand. Every
 * other channel is a record of something booked elsewhere. Sabre and Travelport are twins here —
 * anything that gates on "is this an automated booking" must ask this, not compare to one vendor.
 */
export const AUTOMATED_BOOKING_CHANNELS = ['sabre', 'travelport'] as const satisfies readonly BookingChannel[];

export function isAutomatedBookingChannel(channel: string | null | undefined): boolean {
  return (
    channel !== undefined && channel !== null && (AUTOMATED_BOOKING_CHANNELS as readonly string[]).includes(channel)
  );
}

/**
 * One physical room being booked. Each entry maps 1:1 to an itinerary item and
 * to a single Sabre booking call (with its own confirmation number and
 * cancellation policy). Multi-room bookings of the same room type are modeled
 * as N independent entries, one per itinerary item.
 *
 * `occupancy` is sourced from the rate at checkout-start time and is read-only
 * thereafter — the rate already carries the exact occupancy it was searched
 * and priced for.
 */
export const HotelBookingRoomSchema = z.object({
  itineraryItemId: z.string(),
  roomOptionId: z.string(),
  /**
   * Stable identity (paired with `occupancy`) for looking up the rate on the
   * RoomOption — `selectedRateKey` rotates whenever Sabre returns a fresh
   * search, so anything that needs to find the rate after the fact must use
   * `(productCode, occupancy)` via `RoomOption.getRateByIdentity`.
   */
  productCode: z.string(),
  selectedRateKey: z.string(),
  sabreBookingKey: z.string().optional(),
  occupancy: OccupancySchema,
  selectedExtras: z.array(SelectedRoomExtraSchema).default([]),
  bedTypePreference: z.number().optional(),
  smokingPreference: z.boolean().optional(),
  accessibilityNeeds: z.number().optional(),
  roomNote: z.string().optional(),
  status: HotelBookingRoomStatusSchema.default('pending'),
  confirmationNumber: z.string().optional(),
  cancellationPolicy: z.string().optional(),
  channel: BookingChannelSchema.optional(),
});

export type HotelBookingRoom = z.infer<typeof HotelBookingRoomSchema>;

/**
 * Outcome of an entry-time PriceCheck for a single room. Surfaces price changes,
 * unavailability, and inventory shortfalls so the page can ask the user to
 * acknowledge or adjust before submitting.
 */
export const PriceCheckResultCodeSchema = z.enum([
  'unchanged',
  'priceChanged',
  'unavailable',
  'insufficientInventory',
  // Defensive code: Sabre rejected the rate because `adults + children`
  // exceeded the per-room cap (WARN.0322 "Number of persons exceeded limit.").
  // Occupancy is rate-derived and read-only at checkout, so this should never
  // happen in practice; treat as unavailable and escalate.
  'occupancyExceeded',
]);
export type PriceCheckResultCode = z.infer<typeof PriceCheckResultCodeSchema>;

export const PriceCheckResultSchema = z.object({
  itineraryItemId: z.string(),
  code: PriceCheckResultCodeSchema,
  previousTotal: CostSchema.optional(),
  newTotal: CostSchema.optional(),
  availableQuantity: z.number().int().nonnegative().optional(),
});

export type PriceCheckResult = z.infer<typeof PriceCheckResultSchema>;

/**
 * Server-computed tax/fee/subtotal decomposition of a prepaid total.
 *
 * Mirrors Sabre's convention where `total` (`AmountAfterTax`) is the all-in
 * user-facing total, so `subtotal = total − totalTax − totalFees`. Computed
 * server-side (in `priceCheckOneItem`, where `localPricing` is already in hand)
 * so the client never re-derives the subtraction convention, the per-room clamp,
 * or the mixed-currency fallback. The three components always recompose to
 * `total`.
 */
export const PriceBreakdownSchema = z.object({
  /** `total − totalTax − totalFees`, clamped at 0. */
  subtotal: z.number(),
  totalTax: z.number(),
  totalFees: z.number(),
  /**
   * Labeled tax/fee line items (Sabre `TaxGroups` / `FeeGroups`) frozen alongside the aggregates so a
   * booking record can disclose mandatory fees (e.g. "Resort fee") line-by-line (ELARA-829). Optional —
   * absent on snapshots taken before this field existed or when Sabre itemized nothing.
   */
  taxBreakdown: z.array(LabeledChargeSchema).optional(),
  feeBreakdown: z.array(LabeledChargeSchema).optional(),
  /** All-in total (Sabre `AmountAfterTax`). */
  total: z.number(),
  currency: z.string(),
});

export type PriceBreakdown = z.infer<typeof PriceBreakdownSchema>;

/**
 * Per-room total at the moment the user committed to checkout. Compared against
 * a fresh PriceCheck on submit; any drift forces the user to re-acknowledge.
 *
 * `breakdown` carries the server-computed tax/fee/subtotal split for this room
 * so the checkout summary can render line items without re-deriving them from
 * the live `RoomOption.localPricing`. Optional for backward compatibility with
 * snapshots persisted before this field existed.
 */
export const PriceSnapshotItemSchema = z.object({
  itineraryItemId: z.string(),
  totalCost: CostSchema,
  includesExtras: z.boolean(),
  breakdown: PriceBreakdownSchema.optional(),
});

export type PriceSnapshotItem = z.infer<typeof PriceSnapshotItemSchema>;

/**
 * Provider where a flight was booked externally (Skyscanner, Delta, etc.).
 * Captured at flight checkout and stored on the itinerary item's `booking`.
 */
export const FlightBookingProviderSchema = z.object({
  name: z.string(),
  logo: z.string().optional(),
});

export type FlightBookingProvider = z.infer<typeof FlightBookingProviderSchema>;
